import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState
} from "react";
import { messagesForLocale } from "./messages";
import {
  applyDocumentLocale,
  detectLocale,
  directionForLocale,
  persistLocale
} from "./runtime";
import type { Direction, Locale, MessageCatalog } from "./types";

export type I18nContextValue = {
  locale: Locale;
  dir: Direction;
  messages: MessageCatalog;
  setLocale: (locale: Locale) => void;
};

const fallbackContext: I18nContextValue = {
  locale: "en",
  dir: "ltr",
  messages: messagesForLocale("en"),
  setLocale: () => undefined
};

const I18nContext = createContext<I18nContextValue>(fallbackContext);

export function I18nProvider({
  children,
  initialLocale
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? detectLocale()
  );

  useLayoutEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  const setLocale = useCallback((nextLocale: Locale) => {
    applyDocumentLocale(nextLocale);
    persistLocale(nextLocale);
    setLocaleState(nextLocale);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      dir: directionForLocale(locale),
      messages: messagesForLocale(locale),
      setLocale
    }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
